// How a route card's numbers are written, on the web editor, the share page
// and (mirrored in RouteCards::PdfPresenter) the PDF. The server sends whole
// numbers — metres, degrees, minutes — and never a formatted string, so these
// are the only place the card's notation lives on the web.

const pad2 = (value: number): string => String(value).padStart(2, '0');

/** Clock time from minutes after midnight: 596 -> "09:56". Wraps past midnight. */
export function formatClock(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '';
  const wrapped = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(wrapped / 60))}:${pad2(wrapped % 60)}`;
}

/** A duration as h:mm, the way a route card times a leg: 78 -> "1:18". */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '';
  const whole = Math.max(0, Math.round(minutes));
  return `${Math.floor(whole / 60)}:${pad2(whole % 60)}`;
}

/** A spare-daylight figure as "2h 12m", signed when it runs past sunset. */
export function formatHoursMinutes(minutes: number): string {
  const sign = minutes < 0 ? '-' : '';
  const whole = Math.abs(Math.round(minutes));
  return `${sign}${Math.floor(whole / 60)}h ${pad2(whole % 60)}m`;
}

/** Three-figure bearing, as read off a compass: 5 -> "005°". */
export function formatBearing(degrees: number | null | undefined): string {
  if (degrees === null || degrees === undefined || !Number.isFinite(degrees)) return '';
  const whole = ((Math.round(degrees) % 360) + 360) % 360;
  return `${String(whole).padStart(3, '0')}°`;
}

/** Kilometres to one decimal place: 2604 -> "2.6". */
export function formatKm(metres: number): string {
  return (metres / 1000).toFixed(1);
}

/** "magnetic = grid + 3°" offset, signed and to the nearest degree: -2.6 -> "−3°". */
export function formatOffset(degrees: number | null | undefined): string {
  if (degrees === null || degrees === undefined || !Number.isFinite(degrees)) return '';
  const whole = Math.round(degrees);
  if (whole === 0) return '0°';
  return `${whole > 0 ? '+' : '−'}${Math.abs(whole)}°`;
}

/** "HH:MM" from an <input type="time"> -> minutes after midnight, or null. */
export function parseClock(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}
